import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { useAuth } from "../context/authContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/atoms/LoadingSpinner";
import { Eye, EyeOff, LogIn, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only same-app paths ("/checkin/..."), never absolute/protocol-relative URLs
  const nextParam = searchParams.get("next");
  const next = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/";
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { handleSubmit, register, setError, formState: { errors }, setValue } = useForm();

  const onSubmit = async (userData: FieldValues) => {
    setIsLoading(true);
    try {
      await login(userData.identifier as string, userData.password as string);
      
      // Save email if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', userData.identifier);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      navigate(next);
    } catch (error) {
      // Supabase auth errors
      if (error instanceof Error && error.message) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid email or password')) {
          setError("password", { message: "Credenciales inválidas. Verifica tu usuario y contraseña." });
          setValue("password", "");
        } else if (errorMsg.includes('email not confirmed')) {
          setError("identifier", { message: "La cuenta no está confirmada. Contacta a un administrador." });
        } else if (errorMsg.includes('user not found')) {
          setError("identifier", { message: "El usuario no está registrado en el sistema." });
        } else if (errorMsg.includes('invalid email')) {
          setError("identifier", { message: "El usuario o correo no es válido." });
        } else if (errorMsg.includes('banned')) {
          setError("identifier", { message: "Tu cuenta está desactivada. Contacta a un administrador." });
          setValue("password", "");
        } else if (errorMsg.includes('too many requests')) {
          setError("password", { message: "Demasiados intentos. Espera unos minutos e intenta nuevamente." });
        } else {
          setError("password", { message: "Error al iniciar sesión. Intenta nuevamente." });
          setValue("password", "");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load remembered email on mount
  useState(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setValue('identifier', rememberedEmail);
      setRememberMe(true);
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 px-4 py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-md"
      >
        <Card variant="elevated" className="shadow-2xl">
          <CardHeader className="text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="space-y-4"
            >
              <div className="flex justify-center">
                <img 
                  src="/addbolivialogo.png" 
                  alt="Las Asambleas de Dios de Bolivia" 
                  className="h-20 w-auto"
                  style={{
                  filter: 'drop-shadow(0 0 7px #ffffff)'
                }}
                />
              </div>
              <CardTitle className="bg-gradient-to-r from-primary to-accent bg-clip-text text-3xl font-bold tracking-tight text-transparent">
                Iniciar Sesión
              </CardTitle>
              <CardDescription className="mt-2">
                Accede al portal administrativo
              </CardDescription>
            </motion.div>
          </CardHeader>

        <CardContent>
          {searchParams.get("inactive") && (
            <div role="alert" className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>Tu cuenta está desactivada. Contacta a un administrador.</span>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
            <div className="space-y-2">
              <Label 
                htmlFor="userEmail"
                className={errors.identifier ? "text-destructive" : ""}
              >
                Usuario o correo
              </Label>
              <Input
                id="userEmail"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Tu CI o tu correo"
                disabled={isLoading}
                className={errors.identifier ? "border-destructive" : ""}
                {...register("identifier", {
                  required: "El usuario es un campo requerido."
                })}
              />
              {errors.identifier && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>{errors.identifier.message as string}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label 
                htmlFor="userPwd"
                className={errors.password ? "text-destructive" : ""}
              >
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="userPwd"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  disabled={isLoading}
                  className={errors.password ? "border-destructive pr-10" : "pr-10"}
                  {...register("password", {
                    required: "La contraseña es un campo requerido"
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>{errors.password.message as string}</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                className="size-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
              />
              <Label 
                htmlFor="rememberMe" 
                className="cursor-pointer text-sm font-normal"
              >
                Recordar mi usuario
              </Label>
            </div>

            <Button
              disabled={Object.keys(errors).length > 0 || isLoading}
              type="submit"
              variant="gradient"
              className="w-full"
              size="lg"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 size-4" />
                  Ingresar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
      </motion.div>
    </div>
  );
};

export default Login;
